import { useCallback, useEffect, useMemo, useState } from "react";

import { useForm, useFieldArray } from "react-hook-form";
import { Badge } from "reactstrap";

import {
  Spinner,
  Table,
  TableDashboard,
  FormFooterActions,
  Modal,
} from "@atoms";
import useApiFetch from "@hooks/useApiFetch";
import useQueryParams from "@hooks/useQueryParams";
import useToast from "@hooks/useToast";

const queryParams = [
  "page",
  "search",
  "pageSize",
  "sortPlanName",
  "sortStatus",
];

const RecordkeeperPlanIds = ({
  match: {
    params: { planId },
  },
}) => {
  const [companyDetails, setCompanyDetails] = useState([]);
  const [planName, setPlanName] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [modifiedRows, setModifiedRows] = useState(null);

  const { control, getValues, setValue } = useForm();
  const { get, post, isPending } = useApiFetch();
  const { showErrorToast } = useToast();
  const { getQueryParams, buildQueryParams } = useQueryParams();

  const queryPath = getQueryParams(queryParams);
  const { append, remove } = useFieldArray({
    control,
    name: "companyDetailsTable",
  });

  const { companyDetailsTable = [] } = getValues();

  const getRkCompanies = useCallback(async () => {
    const response = await get(`recordkeepers/GetRKCompanies?PlanID=${planId}`);
    setCompanyDetails(response?.rkPlanData || []);
  }, []);

  useEffect(() => {
    (async () => {
      const response = await get(`plans/${planId}`);
      setPlanName(response?.planName || "");
    })();

    getRkCompanies();
  }, []);

  useEffect(() => {
    if (companyDetails?.length) {
      setValue("companyDetailsTable", companyDetails);
    }
  }, [companyDetails]);

  const getUpdatedRows = (fieldToCompare) => {
    if (!companyDetailsTable?.length) return [];
    return companyDetailsTable?.filter((row) => {
      const rawData = companyDetails.find(
        (item) => item.planCompanyID === row.planCompanyID
      );
      if (!rawData?.planCompanyID) return true;
      return fieldToCompare
        ? row[fieldToCompare] !== rawData[fieldToCompare]
        : row.rkPlanIdentifier !== rawData.rkPlanIdentifier ||
            row.rkCompanyName !== rawData.rkCompanyName;
    });
  };

  const onSubmit = useCallback(
    async (rows) => {
      const payloadValues = {
        rkPlanData: rows.map((item) => ({ ...item, planID: planId })),
      };
      const successMessage = modifiedRows?.totalAccounts
        ? `${modifiedRows?.totalAccounts} RE Accounts updated`
        : "";

      await post("recordkeepers/CreateUpdateRKCompany", payloadValues, {
        successMessage,
      });
      getRkCompanies();
    },
    [modifiedRows]
  );

  const handleSubmit = useCallback(async () => {
    const updatedRows = getUpdatedRows();
    const updatedRowsWithIdentifier = getUpdatedRows("rkPlanIdentifier");

    const allUpdatedRows = [...updatedRows, ...updatedRowsWithIdentifier];

    if (!companyDetailsTable?.length) {
      return showErrorToast("At least one record is required");
    }

    const uniqueArray = allUpdatedRows.reduce((acc, item) => {
      if (!acc.some((r) => r.planCompanyID === item.planCompanyID)) {
        acc.push(item);
      }
      return acc;
    }, []);

    const hasExistingRecords = uniqueArray.some((row) => row.planCompanyID);

    const associatedAccounts = updatedRowsWithIdentifier
      ?.filter((row) => row.rkPlanIdCount)
      ?.reduce((sum, row) => sum + (row.rkPlanIdCount || 0), 0);

    const updatedRowsWithOldIdentifier = uniqueArray.map((updatedRow) => {
      const originalRow = companyDetails.find(
        (detail) => detail.planCompanyID === updatedRow.planCompanyID
      );
      if (updatedRow?.rkPlanIdentifier !== originalRow?.rkPlanIdentifier) {
        return {
          ...updatedRow,
          rkPlanIdentifierOld: originalRow?.rkPlanIdentifier || null,
        };
      }
      return updatedRow;
    });

    if (hasExistingRecords) {
      setIsConfirmModalOpen(true);
      setModifiedRows({
        updatedRowsWithIdentifier: updatedRowsWithOldIdentifier,
        totalAccounts: associatedAccounts,
        hasExistingRecords,
      });
    } else {
      await onSubmit(updatedRows);
    }
  }, [companyDetailsTable, getUpdatedRows]);

  const handleDeleteRow = async (row) => {
    const {
      id: rowIndexId,
      original: { planCompanyID },
    } = row;

    if (companyDetailsTable?.length > 1) {
      if (planCompanyID) {
        const response = await post(
          `recordkeepers/DeleteRKCompany?PlanCompanyID=${planCompanyID}`
        );
        if (response?.status === 200) {
          remove(rowIndexId);
        }
      } else {
        remove(rowIndexId);
      }
    } else {
      showErrorToast("Deletion of all items is not permitted");
    }
  };

  const column = [
    {
      id: "rkPlanIdentifier",
      header: "Recordkeeper Identifier",
      accessorKey: "rkPlanIdentifier",
    },
    {
      id: "rkCompanyName",
      header: "Plan Name",
      accessorKey: "rkCompanyName",
    },
    {
      id: "actions",
      header: "",
      size: 300,
      cell: ({ row }) => {
        const handleDelete = () => handleDeleteRow(row);
        return (
          <div className="pr-2 column-end">
            <Badge
              color="danger"
              className="pointer my-2"
              onClick={handleDelete}
            >
              Delete
            </Badge>
          </div>
        );
      },
    },
  ];

  const tableFooter = useMemo(() => {
    const addNewRow = () => append({ rkPlanIdentifier: "", rkCompanyName: "" });
    const buttonActions = {
      buttonProps: [
        { labelName: "Add", onClick: addNewRow },
        { onClick: handleSubmit },
      ],
    };

    return (
      <FormFooterActions
        linkProps={[
          { path: `/plans/${planId}/edit?${buildQueryParams(queryPath)}` },
        ]}
        {...buttonActions}
      />
    );
  }, [handleSubmit]);

  const handleModalSubmit = () => {
    if (modifiedRows?.hasExistingRecords) {
      onSubmit(modifiedRows?.updatedRowsWithIdentifier);
      setIsConfirmModalOpen(false);
      setModifiedRows(null);
    }
    return;
  };

  const modalButtons = [
    {
      name: "Cancel",
      color: "danger",
      onClick: () => {
        setIsConfirmModalOpen(false);
        setModifiedRows(null);
      },
    },
    {
      name: "Confirm",
      color: "primary",
      onClick: handleModalSubmit,
    },
  ];

  return (
    <TableDashboard pageTitle="Recordkeeper Plan ID's" subtitle={planName}>
      {isConfirmModalOpen ? (
        <Modal
          isModalOpen={isConfirmModalOpen}
          setIsModalOpen={setIsConfirmModalOpen}
          content="All RE Accounts with this RKPlan will be updated"
          variant="danger"
          iconName="lnr-flag"
          modalButtons={modalButtons}
        />
      ) : null}
      <Spinner requesting={isPending}>
        <Table
          columns={column}
          data={companyDetailsTable}
          hidePagination
          footer={tableFooter}
          isEditable
          editableProps={{
            control,
            tableName: "companyDetailsTable",
          }}
        />
      </Spinner>
    </TableDashboard>
  );
};

export default RecordkeeperPlanIds;
